(function () {
  const BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  const SUBSCRIPTION_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';

  function getSettings() {
    return window.klaviyoBisSettings;
  }

  function getModal() {
    return document.getElementById('klaviyo-bis-modal');
  }

  function getProductInfo(trigger) {
    return trigger?.closest('product-info');
  }

  function getVariants(productInfo) {
    const variantsJson = productInfo?.querySelector('[data-klaviyo-bis-variants]')?.textContent?.trim();
    if (!variantsJson) return [];

    try {
      return JSON.parse(variantsJson);
    } catch (error) {
      console.error('[Klaviyo BIS]', error);
      return [];
    }
  }

  function getSelectedVariantId(productInfo) {
    return productInfo?.querySelector('input[name="id"]')?.value || null;
  }

  function formatVariantLabel(variant) {
    if (!variant) return '';
    if (variant.title && variant.title !== 'Default Title') return variant.title;
    return variant.name || `Variant ${variant.id}`;
  }

  function normalizePhoneNumber(phone) {
    const digits = phone.replace(/\D/g, '');
    if (!digits) return null;
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    if (phone.trim().startsWith('+')) return `+${digits}`;
    return `+${digits}`;
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function showMessage(element, message) {
    if (!element) return;
    element.textContent = message;
    element.classList.remove('display--hidden');
  }

  function hideMessage(element) {
    if (!element) return;
    element.textContent = '';
    element.classList.add('display--hidden');
  }

  function populateVariantSelect(select, variants, selectedVariantId) {
    if (!select) return;

    select.innerHTML = '';
    const unavailableVariants = variants.filter((variant) => variant.available === false);
    const options = unavailableVariants.length ? unavailableVariants : variants;

    options.forEach((variant) => {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent = formatVariantLabel(variant);
      if (String(variant.id) === String(selectedVariantId)) {
        option.selected = true;
      }
      select.appendChild(option);
    });

    const variantField = select.closest('[data-klaviyo-bis-variant-field]');
    if (variantField) {
      variantField.classList.toggle('display--hidden', variants.length <= 1);
    }
  }

  function resetModalForm(modal) {
    const form = modal.querySelector('[data-klaviyo-bis-form]');
    const error = modal.querySelector('[data-klaviyo-bis-error]');
    const success = modal.querySelector('[data-klaviyo-bis-success]');
    const submit = modal.querySelector('[data-klaviyo-bis-submit]');

    form?.reset();
    hideMessage(error);
    hideMessage(success);
    if (submit) submit.disabled = false;
  }

  function openModal(trigger) {
    const modal = getModal();
    const settings = getSettings();
    if (!modal || !trigger || trigger.classList.contains('display--hidden')) return;

    const productInfo = getProductInfo(trigger);
    const variants = getVariants(productInfo);
    const selectedVariantId = getSelectedVariantId(productInfo);
    const variantSelect = modal.querySelector('[data-klaviyo-bis-variant-select]');

    resetModalForm(modal);
    populateVariantSelect(variantSelect, variants, selectedVariantId);

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    const emailInput = modal.querySelector('#klaviyo-bis-email');
    emailInput?.focus();
  }

  function closeModal() {
    const modal = getModal();
    if (!modal || modal.hidden) return;

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function buildCatalogVariantId(variantId) {
    return `$shopify:::$default:::${variantId}`;
  }

  async function postJson(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: getSettings()?.apiRevision || '2024-06-15',
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 202 || response.ok) {
      return;
    }

    let detail = getSettings()?.strings?.errorGeneric || 'Something went wrong. Please try again.';

    try {
      const data = await response.json();
      detail = data?.errors?.[0]?.detail || detail;
    } catch (error) {
      console.error('[Klaviyo BIS]', error);
    }

    throw new Error(detail);
  }

  function buildProfileAttributes(email, phone) {
    const attributes = { email };
    if (phone) attributes.phone_number = phone;
    return attributes;
  }

  async function subscribeBackInStock({ account, email, phone, variantId }) {
    const channels = phone ? ['EMAIL', 'SMS'] : ['EMAIL'];
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: buildProfileAttributes(email, phone),
            },
          },
          channels,
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: buildCatalogVariantId(variantId),
            },
          },
        },
      },
    };

    await postJson(`${BIS_ENDPOINT}?company_id=${encodeURIComponent(account)}`, payload);
  }

  async function subscribeSmsConsent({ account, email, phone, smsListId }) {
    // Client API does not accept a `subscriptions` object on profile — consent
    // defaults to MARKETING when omitted. Include both email + phone so Klaviyo
    // links them on a single profile.
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in Stock',
          profile: {
            data: {
              type: 'profile',
              attributes: buildProfileAttributes(email, phone),
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: smsListId,
            },
          },
        },
      },
    };

    await postJson(`${SUBSCRIPTION_ENDPOINT}?company_id=${encodeURIComponent(account)}`, payload);
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const modal = getModal();
    const settings = getSettings();
    const form = event.currentTarget;
    const error = modal?.querySelector('[data-klaviyo-bis-error]');
    const success = modal?.querySelector('[data-klaviyo-bis-success]');
    const submit = modal?.querySelector('[data-klaviyo-bis-submit]');

    if (!modal || !settings) return;

    hideMessage(error);
    hideMessage(success);

    const formData = new FormData(form);
    const email = String(formData.get('email') || '').trim();
    const phoneRaw = String(formData.get('phone') || '').trim();
    const variantId = String(formData.get('variant_id') || '').trim();
    const phone = phoneRaw ? normalizePhoneNumber(phoneRaw) : null;

    if (!variantId) {
      showMessage(error, settings.strings?.errorVariant);
      return;
    }

    if (!isValidEmail(email)) {
      showMessage(error, settings.strings?.errorEmail);
      return;
    }

    if (settings.enableSms && phoneRaw && !phone) {
      showMessage(error, settings.strings?.errorPhone);
      return;
    }

    if (settings.enableSms && phone && !settings.smsListId) {
      showMessage(error, 'SMS list ID is not configured.');
      return;
    }

    if (submit) submit.disabled = true;

    try {
      await subscribeBackInStock({
        account: settings.account,
        email,
        phone: settings.enableSms ? phone : null,
        variantId,
      });

      if (settings.enableSms && phone) {
        await subscribeSmsConsent({
          account: settings.account,
          email,
          phone,
          smsListId: settings.smsListId,
        });
      }

      showMessage(success, settings.strings?.success);
      form.querySelectorAll('input, select, button[type="submit"]').forEach((field) => {
        field.disabled = true;
      });
    } catch (submitError) {
      console.error('[Klaviyo BIS]', submitError);
      showMessage(error, submitError.message || settings.strings?.errorGeneric);
      if (submit) submit.disabled = false;
    }
  }

  function toggleBisTrigger(productInfo, variant) {
    const trigger = productInfo.querySelector('.klaviyo-bis-trigger');
    if (!trigger) return;

    const show = variant && variant.available === false;
    trigger.classList.toggle('display--hidden', !show);
  }

  function syncBisTriggers(productInfo) {
    const variantJson = productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent?.trim();
    if (!variantJson) return;

    try {
      toggleBisTrigger(productInfo, JSON.parse(variantJson));
    } catch (error) {
      console.error('[Klaviyo BIS]', error);
    }
  }

  function openBisFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const shouldOpen = window.location.hash === '#notify-when-available' || params.has('bis');
    if (!shouldOpen) return;

    window.setTimeout(() => {
      const trigger = document.querySelector('.klaviyo-bis-trigger:not(.display--hidden)');
      if (trigger) openModal(trigger);
    }, 600);
  }

  function bindModalEvents() {
    const modal = getModal();
    if (!modal || modal.dataset.klaviyoBisBound === 'true') return;

    modal.dataset.klaviyoBisBound = 'true';

    modal.querySelectorAll('[data-klaviyo-bis-close]').forEach((element) => {
      element.addEventListener('click', closeModal);
    });

    modal.querySelector('[data-klaviyo-bis-form]')?.addEventListener('submit', handleSubmit);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal && !modal.hidden) {
        closeModal();
      }
    });
  }

  function onReady() {
    if (!getSettings()) return;

    bindModalEvents();
    document.querySelectorAll('product-info').forEach(syncBisTriggers);
    openBisFromUrl();
  }

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('.klaviyo-bis-trigger');
      if (!trigger || trigger.classList.contains('display--hidden')) return;

      event.preventDefault();
      openModal(trigger);
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { sectionId, variant } }) => {
      const productInfo =
        document.querySelector(`product-info[data-section="${sectionId}"]`) ||
        document.getElementById(`MainProduct-${sectionId}`);

      if (!productInfo) return;
      toggleBisTrigger(productInfo, variant);
    });
  }
})();
